import { useGetBreeds } from '@application/api';
import { customOrderedAnimalTypeOptions, useModal, useRouter } from '@application/utils';
import { Analytics } from '@application/utils/analytics';
import { AnimalStatCreatedFromEnum } from '@gql/graphql';
import type { BuyerAnimalFormValues } from '@presentation/components';
import { BuyerAnimalForm, ConfirmationModal } from '@presentation/components';
import { MtAnimalBuyerPage } from '@presentation/pages';
import type { FormMode } from '@presentation/types';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useAnimalInitialValuesBuilder } from './api/useAnimalIntialValuesBuilder';
import { useCreateAnimal } from './api/useCreateAnimal';
import { useGetAnimal } from './api/useGetAnimal';
import { useUpdateAnimal } from './api/useUpdateAnimal';
import { useValidateAnimalForm } from './useValidateAnimalForm';

export const MtAnimalBuyerPageController: FC = () => {
  const { infoFromCurrentPath, translate, navigateToRoute } = useRouter();
  const { modal, show, close } = useModal();
  const { modal: modalUnarchive, show: showUnarchive, close: closeUnarchive } = useModal();

  const id = useMemo(() => {
    const idRaw = infoFromCurrentPath.params[0];
    if (!idRaw || idRaw === 'new') return;
    return Number(idRaw);
  }, [infoFromCurrentPath.params]);

  const [modeState, setMode] = useState<FormMode>('view');

  const { validateAnimalForm } = useValidateAnimalForm();

  const { animal, animalLoading } = useGetAnimal(id);
  const { breedOptionsWithCrossbreedsFirst, breedsLoading } = useGetBreeds();
  const { createAnimalFromForm } = useCreateAnimal();
  const { updateAnimalFromForm, updateAnimalArchive, updateAnimalUnarchive } = useUpdateAnimal();
  const { animalInitialValuesBuilder } = useAnimalInitialValuesBuilder();

  const mode = useMemo((): FormMode => {
    if (!id || !animal) return 'new';
    if (animal.archived) return 'view';
    if (!animal.name) return 'edit';
    return modeState;
  }, [id, animal, modeState]);

  const animalTypes = customOrderedAnimalTypeOptions(translate);

  const formSubmitHandler = async (values: BuyerAnimalFormValues) => {
    if (mode === 'new') {
      await createAnimalFromForm({
        form: values,
        createdFrom: AnimalStatCreatedFromEnum.MANUAL_BUYER,
      });
      Analytics.sendAnimalCreatedEvent(values.type, 'manual');
    } else {
      await updateAnimalFromForm(values, animal);
    }

    navigateToRoute('mtAnimals');
  };

  const deleteConfirmHandler = useCallback(async () => {
    close();
    await updateAnimalArchive(animal?.id);
    navigateToRoute('mtAnimals');
  }, [animal?.id, close, navigateToRoute, updateAnimalArchive]);

  const unarchiveConfirmHandler = useCallback(async () => {
    closeUnarchive();
    await updateAnimalUnarchive(animal?.id);
    navigateToRoute('mtAnimals');
  }, [animal?.id, closeUnarchive, navigateToRoute, updateAnimalUnarchive]);

  return (
    <>
      <ConfirmationModal
        modal={modal}
        onConfirm={deleteConfirmHandler}
        title={translate('common.modals.archiveAnimal.title')}
        text={translate('common.modals.archiveAnimal.text')}
      />
      <ConfirmationModal
        modal={modalUnarchive}
        onConfirm={unarchiveConfirmHandler}
        title={translate('common.modals.unarchiveAnimal.title')}
        text={translate('common.modals.unarchiveAnimal.text')}
      />
      <MtAnimalBuyerPage
        loading={breedsLoading || animalLoading}
        archived={animal?.archived}
        onBackClick={() => navigateToRoute('mtAnimals')}
        mode={mode}
        onEditClick={() => {
          setMode('edit');
        }}
        onUnarchiveClick={() => showUnarchive()}
        onDeleteClick={() => show()}
        formComponent={
          <BuyerAnimalForm
            mode={mode}
            onCancelClick={() => {
              if (mode === 'new') {
                navigateToRoute('mtAnimals');
              } else {
                setMode('view');
              }
            }}
            imageLabel={translate('common.inputs.animalImageTransferred.label')}
            form={{
              onSubmit: formSubmitHandler,
              validate: validateAnimalForm,
              initialValues: animalInitialValuesBuilder(mode !== 'new' ? animal : undefined),
              options: {
                breeds: breedOptionsWithCrossbreedsFirst,
                animalTypes,
              },
            }}
          />
        }
      />
    </>
  );
};
